import Complement from "@domain/composition/Complement";
import Base from "../composition/Base";
import Light from '../composition/Light';
import should from 'should';

export interface RuleReason {
    es?: string,
    en?: string,
    it?: string,
    de?: string,
    fr?: string,
    us?: string,
}

export interface RuleData {
    rule: string,
    reason?: RuleReason
}

export const RuleResult = { "accept": "accept", "reject": "reject", "pass": "pass" }
export type RuleResultType = "accept" | "reject" | "pass"

export default class Rule {
    ruleData: RuleData
    element: Base
    constructor(ruleData: RuleData, element: Base) {
        this.ruleData = ruleData
        this.element = element
    }

    evaluate() {
        try {
            var ruleFn = new Function("should", this.ruleData.rule + ";return this.pass();");
            const result = ruleFn.call(this, should);
            return result
        } catch (e) {
            if (e.actual) { // Check if should throw error
                return this.reject()
            }
            return this.pass()
        }
    }

    getReason() {
        return this.ruleData.reason ?? this.ruleData.rule
    }

    accept(): RuleResultType {
        return "accept"
    }

    reject(): RuleResultType {
        return "reject"
    }

    pass(): RuleResultType {
        return "pass"
    }

    checkExclusions(): RuleResultType {
        let elementcheck
        this.element.getClassName() === "Light"
            ? elementcheck = this.element as Light
            : elementcheck = this.element as Complement
        const color = elementcheck.getAttributeVariation('color')?.modifier ?? '*';
        const temperature = elementcheck.getAttributeVariation('temperature')?.modifier ?? '*';
        const control = elementcheck.getAttributeVariation('control')?.modifier ?? '*';
        const installation1 = elementcheck.getAttributeVariation('installation')?.modifier[0] ?? '*';
        const installation2 = elementcheck.getAttributeVariation('installation')?.modifier[1] ?? '*';
        // const pendantHeight = (elementcheck as Pendant).getAttribute('wireLength') ?? '*';
        for (const iteratorExclusion of this.element.data.exclusion) {
            if ((iteratorExclusion.Acabado !== "*") && (iteratorExclusion.Acabado !== color)) {
                continue;
            }
            if ((iteratorExclusion.Led !== "*") && (iteratorExclusion.Led !== temperature)) {
                continue;
            }
            if ((iteratorExclusion.Dim !== "*") && (iteratorExclusion.Dim !== control)) {
                continue;
            }
            if ((iteratorExclusion.Instalacion1 !== "*") && (iteratorExclusion.Instalacion1 !== installation1)) {
                continue;
            }
            if ((iteratorExclusion.Instalacion2 !== "*") && (iteratorExclusion.Instalacion2 !== installation2)) {
                continue;
            }
            // if ((iteratorExclusion.Longitud !== "*") && (iteratorExclusion.Longitud !== pendantHeight)) {
            //     continue;
            // }
            //if ((iteratorExclusion.Adaptacion !== "*") && (iteratorExclusion.Adaptacion !== this.element.getAttribute(""))) {
            //continue;
            //}

            if (iteratorExclusion.Acabado === "*" &&
                iteratorExclusion.Led === "*" &&
                iteratorExclusion.Dim === "*" &&
                iteratorExclusion.Instalacion1 === "*" &&
                iteratorExclusion.Instalacion2 === "*"
                // iteratorExclusion.Longitud === "*" &&
                // iteratorExclusion.Adaptacion === "*"
            ) {
                continue;
            }

            return this.reject();
        }

        return this.pass();
    }

    _equal(param1, param2): RuleResultType {
        let result = this.pass();

        if (param1 !== param2) {
            result = this.reject()
        }

        return result;
    }

    _sum(param1, param2) {
        return param1 + param2;
    }

    _getModel(article?) {
        const element = article ?? this.element
        if (!element.instanceofType("Complement") && !element.instanceofType("Light")) {
            return null
        } else {
            return element.getModel()
        }
    }

    _getAttribute(attributeName) {
        let elementcheck
        this.element.getClassName() === "Light"
            ? elementcheck = this.element as Light
            : elementcheck = this.element as Complement

        let attributeValue = '';
        if (attributeName === "watts") {
            attributeValue = elementcheck.getInfo()?.watts || 0
        } else if (attributeName === "installation") {
            attributeValue = elementcheck.getAttribute("installation", true) || ""
        } else if (attributeName === "control") {
            attributeValue = elementcheck.getAttribute("control", true) || ""
        } else if (attributeName === "productLength") {
            attributeValue = elementcheck.getArticleSummary?.().productLength?.value || ""
        }
        return attributeValue;
    }

    maxChildrenOfType(nameComponent, maxNumber) {
        //@ts-ignore
        let elementsByType = this.element.getElementsByType(nameComponent);
        elementsByType.length.should.be.lessThanOrEqual(maxNumber)
    }

    _getChildrenOfType(type) {
        return this.element.getAllElementsByType(type)
    }

    _getNumberOfTypeChildren(type) {
        //@ts-ignore
        return this.element.getAllElementsByType(type).length
    }

    _isElementOfType(type) {
        return this.element.getClassName() === type
    }

    _getApplicationLocation() {
        const track = this.element?.getAllElementsByType("Track")
        const applicationLocation = track?.[0]?.getApplicationLocationId()
        return applicationLocation;
    }

    _getProductTypology(article) {
        const element = article ?? this.element
        if (!element.instanceofType("Complement") && !element.instanceofType("Light")) {
            return null
        } else {
            return element.getSpecificProductTypology().id
        }
    }

    _getElementsAttributesByType(type, attributeName) {
        const listOfElements = this.element.getAllElementsByType(type)

        const result = [];

        listOfElements.forEach(element => {
            let value = null
            if (attributeName === "installation") {
                value = element.getAttributeModifier("installation") || null
            } else if (attributeName === "control") {
                value = element.getAttributeModifier("control") || null
            } else if (attributeName === "productLength") {
                value = element.getTechnicalDataByKey("productLength")?.value || null
            } else if (attributeName === "deliveredPower" || attributeName === "consumedPower") {
                value = element.getInfo()?.watts || null
            } else if (attributeName === "weight") {
                value = element.getTechnicalDataByKey('packaging')?.netWeight || null
            } else if (attributeName === "secondaryFinish") {
                value = element.getTechnicalDataByKey("secondaryFinish")?.id || null
            } else if (attributeName === "finish") {
                value = element.getAttributeModifier("color")
            }

            if (value) {
                result.push(value)
            }
        })

        return result
    }

    _getNumberOfLightsByLightingEffects(lightingEffectId, level) {
        let elementsByType = this.element.getAllElementsByType('Light') as Light[]

        const lights = elementsByType.filter(light => {
            return light.getLightingEffect()?.[`lvl${level}`]?.id == lightingEffectId
        })

        return lights.length
    }

}
